import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDate } from '@utils/FormatUtils';
import type { Popup } from '../../models/Popup';
import { getPopupLocalizedContent } from '../../utils/popupLocale';
import { getLastVisibleDate } from '../../utils/expiresAt';
import { DropdownActions } from '../interaction/DropdownActions';

interface PopupListRowProps {
  popup: Popup;
  isLast: boolean;
  onPreview: (popup: Popup) => void;
  onViewEvents: (popup: Popup) => void;
  onArchive: (popup: Popup) => void;
  onRestore: (popup: Popup) => void;
  onDelete: (popup: Popup) => void;
}

export const PopupListRow = memo(function PopupListRow({
  popup,
  isLast,
  onPreview,
  onViewEvents,
  onArchive,
  onRestore,
  onDelete,
}: PopupListRowProps) {
  const { t, i18n } = useTranslation('popups');

  const title = getPopupLocalizedContent(popup, i18n.resolvedLanguage ?? i18n.language).title;
  const frequencyLabel = t(`filters.frequency.${popup.frequency}`);
  const lastVisibleDateLabel = popup.expires_at ? formatDate(getLastVisibleDate(popup.expires_at)) : '-';

  return (
    <div
      className={`grid grid-cols-[1fr_auto_2.5rem] items-center gap-3 px-4 py-3.5 sm:py-4 ${
        isLast ? '' : 'border-b border-border'
      }`}
      data-testid={`popup-row-${popup.id}`}
    >
      <div className="min-w-0 pr-1">
        <p className="truncate text-sm font-medium text-foreground">{title}</p>
        <p className="mt-0.5 truncate text-sm text-muted-foreground">
          {t('list.expiresAt', { date: lastVisibleDateLabel })}
        </p>
      </div>

      <span className="max-w-[7rem] shrink-0 truncate rounded-full border border-border bg-muted/40 px-2.5 py-1 text-center text-xs font-medium text-foreground sm:max-w-[8rem]">
        {frequencyLabel}
      </span>

      <div className="flex justify-end">
        <DropdownActions
          popup={popup}
          onPreview={onPreview}
          onViewEvents={onViewEvents}
          onArchive={onArchive}
          onRestore={onRestore}
          onDelete={onDelete}
        />
      </div>
    </div>
  );
});
